import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { BoxShadowModifierValue } from "schemas/generated/fileModifiers";

import React, { useMemo } from "react";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import {
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

const SHADOW_OPTIONS = [
  {
    value: "none",
    label: "None",
    displayValue: "None",
  },
  {
    value: "2xs",
    label: "2XS",
    displayValue: "2XS",
  },
  {
    value: "xs",
    label: "XS",
    displayValue: "XS",
  },
  {
    value: "sm",
    label: "Small",
    displayValue: "Small",
  },
  {
    value: "md",
    label: "Medium",
    displayValue: "Medium",
  },
  {
    value: "lg",
    label: "Large",
    displayValue: "Large",
  },
  {
    value: "xl",
    label: "Extra Large",
    displayValue: "Extra Large",
  },
  {
    value: "2xl",
    label: "2XL",
    displayValue: "2XL",
  },
] as const;

interface ShadowModifierProps {
  onChange: (modifiers: BoxShadowModifierValue[]) => void;
}

const ShadowModifierComponent: React.FC<ShadowModifierProps> = ({
  onChange,
}) => {
  const extractedValue = useSemanticModifierValue({
    semanticExtractor: ({ semanticElement }) => {
      const className = semanticElement.cssClasses;
      if (className && typeof className === "string") {
        const classes = className.split(" ").filter(Boolean);

        for (const cls of classes) {
          if (cls === "shadow") {
            return "md";
          }
          if (cls.startsWith("shadow-")) {
            const shadowValue = cls.replace("shadow-", "");
            if (SHADOW_OPTIONS.some((option) => option.value === shadowValue)) {
              return shadowValue;
            }
          }
        }
      }
      return "none";
    },
  });

  const currentExtracted = useMemo(
    () => extractedValue ?? "none",
    [extractedValue],
  );

  const [currentValue, setCurrentValue] = useOverridableState(
    currentExtracted,
    useDebouncedCallback((shadowValue: string) => {
      if (SHADOW_OPTIONS.some((option) => option.value === shadowValue)) {
        onChange([
          {
            type: "boxShadow" as const,
            value: shadowValue as BoxShadowModifierValue["value"],
          },
        ]);
      }
    }, 300),
  );

  const items: ComboboxItemWithValue[] = SHADOW_OPTIONS.map((shadow) => ({
    value: shadow.value,
    label: shadow.label,
  }));

  const selectedItem =
    items.find((item) => item.value === currentValue) ?? null;

  const isPlaceholder = currentValue === null || currentValue === "none";

  return (
    <div className="shadow-modifier">
      <ComboboxRoot
        items={items}
        value={selectedItem}
        onValueChange={(item) => {
          setCurrentValue(item?.value ?? "none");
        }}
        itemToStringValue={(item) => String(item.label)}
        isItemEqualToValue={(a, b) => a.value === b.value}
        filter={null}
      >
        <ComboboxTriggerButton className="w-full" isPlaceholder={isPlaceholder}>
          {selectedItem?.label ?? "None"}
        </ComboboxTriggerButton>
        <ComboboxContent sideOffset={8} side="top" align="center">
          <ComboboxList>
            {(item: ComboboxItemWithValue) => (
              <ComboboxItem key={item.value} {...item} value={item} />
            )}
          </ComboboxList>
        </ComboboxContent>
      </ComboboxRoot>
    </div>
  );
};

export default ShadowModifierComponent;
